/**
 * EmployeeOnboardingWizard — LBR2-C3 (2026-07-26)
 *
 * Multi-step onboarding wizard that creates a linked User + Employee (via the
 * backend `POST /employees/onboard` → C1's createLinkedEmployeeUser), captures
 * the rich KYC fields, and uploads KYC documents (PAN/Aadhaar/photo) through the
 * shared `/api/v1/files` service, attaching each as an EmployeeKycDocument.
 */
import { useEffect, useState } from 'react';
import { X, Loader2, ChevronLeft, ChevronRight, Check, Upload, Trash2 } from 'lucide-react';
import { api } from '@/utils/api';
import {
  ONBOARDING_STEPS,
  emptyOnboardingForm,
  validateStep,
  canSubmit,
  kycDocTypeLabel,
  KYC_DOC_TYPES,
  MIN_PASSWORD_LENGTH,
  MARITAL_STATUS_OPTIONS,
  BLOOD_GROUP_OPTIONS,
  type OnboardingForm,
  type OnboardingStepId,
  type KycDocType,
  type WizardMode,
} from './employeeOnboardingSteps';

interface RoleRecord { id: string; displayName?: string; name?: string }
interface DeptRecord { id: string; name: string }
interface StagedDoc { docType: KycDocType; file: File; label: string }
interface ExistingDoc { id: string; docType: string; label?: string | null; status?: string | null }

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Fires after BOTH a create and an edit — the caller only ever wants "the
   * list is stale, refetch it", so a second callback would be two names for
   * one job.
   */
  onCreated: () => void;
  /**
   * 2026-09-05 — absent = CREATE (unchanged behaviour). Present = EDIT: the same
   * five steps, prefilled from the record, saved with `PUT /employees/:id`.
   *
   * Manak, 2026-09-05: "same modal as add employee, all prefilled, editable and
   * uploadable feeds as is in add wizard, warna kaise update hote." The full
   * wizard moves INTO Edit; the old six-field drawer is gone, and Add is NOT
   * reduced to it.
   */
  employeeId?: string | null;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

export function EmployeeOnboardingWizard({ open, onClose, onCreated, employeeId }: Props) {
  const mode: WizardMode = employeeId ? 'edit' : 'create';
  const [form, setForm] = useState<OnboardingForm>({ ...emptyOnboardingForm });
  const [stepIdx, setStepIdx] = useState(0);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [departments, setDepartments] = useState<DeptRecord[]>([]);
  const [docs, setDocs] = useState<StagedDoc[]>([]);
  const [existingDocs, setExistingDocs] = useState<ExistingDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 🔒 B47 (2026-10-12): a fresh step is not "in error". The list shows once
  // the person tries to go on (Next / Create) and resets on every step change.
  const [attempted, setAttempted] = useState(false);
  useEffect(() => { setAttempted(false); }, [stepIdx, open]);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyOnboardingForm });
    setStepIdx(0);
    setDocs([]);
    setExistingDocs([]);
    setError(null);
    api.get('/auth/roles').then((r) => setRoles(r.data.data ?? [])).catch(() => {});
    api.get('/departments').then((r) => setDepartments(r.data.data ?? [])).catch(() => {});

    // EDIT — prefill from the record. Nothing below runs in create mode.
    if (!employeeId) return;
    setLoading(true);
    api
      .get(`/employees/${employeeId}`)
      .then((r) => {
        const e = r.data?.data ?? {};
        // Dates arrive as ISO; <input type="date"> wants yyyy-mm-dd.
        const day = (v: unknown) => (v ? String(v).slice(0, 10) : '');
        setForm({
          ...emptyOnboardingForm,
          firstName: e.firstName ?? '',
          lastName: e.lastName ?? '',
          email: e.email ?? '',
          // 🔒 NEVER prefill the password. The server stores a hash, so there is
          // nothing to show, and a masked placeholder would be saved back as a
          // literal new password. Blank means "leave it alone" (see validateStep).
          password: '',
          employeeCode: e.employeeCode ?? '',
          phone: e.phone ?? '',
          designation: e.designation ?? '',
          departmentId: e.departmentId ?? '',
          dateOfJoining: day(e.dateOfJoining),
          salary: e.salary != null ? String(e.salary) : '',
          dateOfBirth: day(e.dateOfBirth),
          gender: e.gender ?? '',
          maritalStatus: e.maritalStatus ?? '',
          bloodGroup: e.bloodGroup ?? '',
          // PAN lives in `pan` with `panNumber` as a legacy mirror; Aadhaar's
          // full number is in `aadharNumber` — ONE 'a', the column really is
          // spelled that way. Read both spellings so older rows still prefill.
          pan: e.pan ?? e.panNumber ?? '',
          aadhaarNumber: e.aadharNumber ?? e.aadhaarNumber ?? '',
          passportNumber: e.passportNumber ?? '',
          personalEmail: e.personalEmail ?? '',
          bankName: e.bankName ?? '',
          bankAccountNo: e.bankAccountNo ?? '',
          currentAddress: e.currentAddress ?? '',
          permanentAddress: e.permanentAddress ?? '',
          personToBeContacted: e.personToBeContacted ?? '',
          emergencyPhoneNumber: e.emergencyPhoneNumber ?? '',
          emergencyRelation: e.emergencyRelation ?? '',
          vehicleRegNo: e.vehicleRegNo ?? '',
          vehicleType: e.vehicleType ?? '',
          fuelType: e.fuelType ?? '',
        });
      })
      .catch(() => setError('Could not load this employee'))
      .finally(() => setLoading(false));

    // What is already on file, so HR can see it before adding more.
    api
      .get(`/employees/${employeeId}/kyc-documents`)
      .then((r) => setExistingDocs(r.data?.data ?? []))
      .catch(() => {});
  }, [open, employeeId]);

  if (!open) return null;

  const step = ONBOARDING_STEPS[stepIdx];
  const stepErrors = validateStep(step.id as OnboardingStepId, form, mode);
  const isLast = stepIdx === ONBOARDING_STEPS.length - 1;
  const set = (k: keyof OnboardingForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (stepErrors.length) { setAttempted(true); return; } // block advancing on invalid step
    setStepIdx((i) => Math.min(i + 1, ONBOARDING_STEPS.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const addDoc = (docType: KycDocType, file: File | null) => {
    if (!file) return;
    setDocs((d) => [...d, { docType, file, label: file.name }]);
  };
  const removeDoc = (i: number) => setDocs((d) => d.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!canSubmit(form, mode)) {
      setStepIdx(0);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let savedId: string;
      if (mode === 'edit') {
        // 🔒 NOT `PUT /employees/:id`. That route validates with
        // `createEmployeeSchema.partial()`, and zod strips unknown keys
        // silently — bank, both addresses, the whole emergency contact,
        // marital status, blood group, passport and personal email were
        // accepted here, sent, and thrown away with no error. HR saw "saved"
        // and nothing changed. `PUT /employees/:id/onboarding` takes the
        // wizard's OWN vocabulary and runs it through the same mapper the
        // create path uses, so the two cannot drift.
        //
        // 🔒 That is also why there is no `aadhaarNumber` → `aadharNumber`
        // rename here any more: the endpoint takes the two-'a' form spelling
        // and does the column translation itself, exactly as it does on
        // create. Renaming on the way out would now MISS the mapper.
        const { password, ...rest } = form;
        await api.put(`/employees/${employeeId}/onboarding`, {
          ...rest,
          salary: form.salary === '' ? undefined : form.salary,
          // 🔒 Only send a password HR actually typed. Blank means "leave it
          // alone" — sending '' would read as a deliberate reset to an empty
          // password. Same bar as the validator, from the same constant.
          ...(password.trim().length >= MIN_PASSWORD_LENGTH ? { password } : {}),
        });
        savedId = employeeId as string;
      } else {
        const res = await api.post('/employees/onboard', {
          ...form,
          salary: form.salary || undefined,
        });
        savedId = res.data?.data?.employee?.id;
      }
      // Upload + attach any staged KYC documents (best-effort per doc). In edit
      // mode this is the id we already had — no create call.
      for (const doc of docs) {
        try {
          const fd = new FormData();
          fd.append('file', doc.file);
          fd.append('entity', 'employee-kyc');
          fd.append('entityId', savedId);
          const up = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const fileUploadId: string = up.data?.data?.id;
          await api.post(`/employees/${savedId}/kyc-documents`, {
            docType: doc.docType,
            fileUploadId,
            label: doc.label,
          });
        } catch {
          // a failed doc upload should not lose the created employee
        }
      }
      onCreated();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          (mode === 'edit' ? 'Failed to save changes' : 'Failed to onboard employee'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + step rail */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit employee' : 'Onboard employee'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="flex flex-wrap gap-1 border-b border-gray-100 px-5 py-3">
          {ONBOARDING_STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => (i < stepIdx || stepErrors.length === 0 ? setStepIdx(i) : undefined)}
              className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs ${
                i === stepIdx
                  ? 'bg-[var(--ink-0)] text-white'
                  : i < stepIdx
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {i < stepIdx && <Check size={12} />}
              {s.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <p className="mb-3 flex items-center gap-2 text-xs text-gray-500">
              <Loader2 size={14} className="animate-spin" /> Loading this employee…
            </p>
          )}
          {step.id === 'identity' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>First name *</label><input className={inputCls} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></div>
              <div><label className={labelCls}>Last name *</label><input className={inputCls} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} /></div>
              <div>
                <label className={labelCls}>Login email *</label>
                <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
                {mode === 'edit' && (
                  <p className="mt-1 text-xs text-gray-500">This is the address they sign in with.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>{mode === 'edit' ? 'New password' : 'Temporary password *'}</label>
                <input className={inputCls} type="text" value={form.password} onChange={(e) => set('password', e.target.value)} />
                {mode === 'edit' && (
                  <p className="mt-1 text-xs text-gray-500">Leave blank to keep the current password.</p>
                )}
              </div>
              {/* 🔒 Role is a CREATE-only field. The role lives on the linked User,
                  `GET /employees/:id` does not return it and `PUT /employees/:id`
                  does not take it — an editable picker here would be a control
                  that silently does nothing. */}
              {mode === 'create' && (
                <div>
                  <label className={labelCls}>Role *</label>
                  <select className={inputCls} value={form.roleId} onChange={(e) => set('roleId', e.target.value)}>
                    <option value="">Select role…</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.displayName || r.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className={labelCls}>Department</label>
                <select className={inputCls} value={form.departmentId} onChange={(e) => set('departmentId', e.target.value)}>
                  <option value="">—</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Employee code</label><input className={inputCls} value={form.employeeCode} onChange={(e) => set('employeeCode', e.target.value)} /></div>
              <div><label className={labelCls}>Designation</label><input className={inputCls} value={form.designation} onChange={(e) => set('designation', e.target.value)} /></div>
              <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
              <div><label className={labelCls}>Date of joining</label><input className={inputCls} type="date" value={form.dateOfJoining} onChange={(e) => set('dateOfJoining', e.target.value)} /></div>
              <div><label className={labelCls}>Monthly salary (₹)</label><input className={inputCls} type="number" value={form.salary} onChange={(e) => set('salary', e.target.value)} /></div>
            </div>
          )}

          {step.id === 'personal' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Date of birth</label><input className={inputCls} type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} /></div>
              <div><label className={labelCls}>Gender</label><input className={inputCls} value={form.gender} onChange={(e) => set('gender', e.target.value)} /></div>
              {/* 🔒 These two are POSTGRES ENUMS, not free text. As plain inputs a
                  typed "Married" or "B+" threw a Prisma validation error and lost
                  the ENTIRE save, not just the field. A <select> makes an invalid
                  value unemittable; blank stays allowed (both columns optional). */}
              <div>
                <label className={labelCls}>Marital status</label>
                <select className={inputCls} value={form.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)}>
                  <option value="">—</option>
                  {MARITAL_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Blood group</label>
                <select className={inputCls} value={form.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)}>
                  <option value="">—</option>
                  {BLOOD_GROUP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>PAN</label><input className={inputCls} value={form.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} placeholder="ABCPK1234F" /></div>
              <div><label className={labelCls}>Aadhaar number</label><input className={inputCls} value={form.aadhaarNumber} onChange={(e) => set('aadhaarNumber', e.target.value)} /></div>
              <div><label className={labelCls}>Passport number</label><input className={inputCls} value={form.passportNumber} onChange={(e) => set('passportNumber', e.target.value)} /></div>
              <div><label className={labelCls}>Personal email</label><input className={inputCls} type="email" value={form.personalEmail} onChange={(e) => set('personalEmail', e.target.value)} /></div>
            </div>
          )}

          {step.id === 'bank' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Bank name</label><input className={inputCls} value={form.bankName} onChange={(e) => set('bankName', e.target.value)} /></div>
              <div><label className={labelCls}>Account number</label><input className={inputCls} value={form.bankAccountNo} onChange={(e) => set('bankAccountNo', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Current address</label><textarea className={inputCls} rows={2} value={form.currentAddress} onChange={(e) => set('currentAddress', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Permanent address</label><textarea className={inputCls} rows={2} value={form.permanentAddress} onChange={(e) => set('permanentAddress', e.target.value)} /></div>
              <div><label className={labelCls}>Emergency contact name</label><input className={inputCls} value={form.personToBeContacted} onChange={(e) => set('personToBeContacted', e.target.value)} /></div>
              <div><label className={labelCls}>Emergency phone</label><input className={inputCls} value={form.emergencyPhoneNumber} onChange={(e) => set('emergencyPhoneNumber', e.target.value)} /></div>
              <div><label className={labelCls}>Emergency relation</label><input className={inputCls} value={form.emergencyRelation} onChange={(e) => set('emergencyRelation', e.target.value)} /></div>
            </div>
          )}

          {step.id === 'vehicle' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <p className="sm:col-span-2 text-xs text-gray-500">For fuel &amp; Vehicle-maintenance reimbursement (flat ₹/day TA/DA — no odometer/GPS).</p>
              <div><label className={labelCls}>Vehicle reg. no.</label><input className={inputCls} value={form.vehicleRegNo} onChange={(e) => set('vehicleRegNo', e.target.value.toUpperCase())} placeholder="HR06 AZ 1234" /></div>
              <div>
                <label className={labelCls}>Vehicle type</label>
                <select className={inputCls} value={form.vehicleType} onChange={(e) => set('vehicleType', e.target.value)}>
                  <option value="">—</option>
                  <option value="car">Car</option>
                  <option value="bike">Bike</option>
                  <option value="scooter">Scooter</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Fuel type</label>
                <select className={inputCls} value={form.fuelType} onChange={(e) => set('fuelType', e.target.value)}>
                  <option value="">—</option>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="cng">CNG</option>
                  <option value="ev">EV</option>
                </select>
              </div>
            </div>
          )}

          {step.id === 'documents' && (
            <div className="space-y-4">
              {/* Edit only — what is already on file. HR should see it before
                  adding more, or the same PAN card gets uploaded three times. */}
              {mode === 'edit' && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="mb-2 text-xs font-medium text-gray-700">Already on file</p>
                  {existingDocs.length === 0 ? (
                    <p className="text-xs text-gray-500">Nothing uploaded yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {existingDocs.map((d) => (
                        <span key={d.id} className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
                          {KYC_DOC_TYPES.includes(d.docType as KycDocType)
                            ? kycDocTypeLabel(d.docType as KycDocType)
                            : d.docType}
                          {d.label ? ` — ${d.label}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {KYC_DOC_TYPES.map((t) => (
                  <label key={t} className="flex cursor-pointer items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs hover:bg-gray-50">
                    <Upload size={13} /> {kycDocTypeLabel(t)}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,application/pdf"
                      onChange={(e) => { addDoc(t, e.target.files?.[0] ?? null); e.currentTarget.value = ''; }}
                    />
                  </label>
                ))}
              </div>
              {docs.length === 0 && <p className="text-xs text-gray-500">No documents staged. Uploads are optional; each starts as "pending" and can be verified later.</p>}
              <ul className="space-y-1">
                {docs.map((d, i) => (
                  <li key={i} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <span><span className="font-medium">{kycDocTypeLabel(d.docType)}</span> — {d.label}</span>
                    <button onClick={() => removeDoc(i)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                Review: <span className="font-medium">{form.firstName} {form.lastName}</span> · {form.email} ·{' '}
                {mode === 'edit'
                  ? `${departments.find((d) => d.id === form.departmentId)?.name || 'no department'}`
                  : `role ${roles.find((r) => r.id === form.roleId)?.displayName || form.roleId || '—'}`}
              </div>
            </div>
          )}

          {attempted && stepErrors.length > 0 && (
            <ul className="mt-3 list-disc rounded-lg bg-red-50 px-6 py-2 text-xs text-red-700">
              {stepErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
          {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
          <button onClick={back} disabled={stepIdx === 0} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-40">
            <ChevronLeft size={14} /> Back
          </button>
          {isLast ? (
            <button onClick={submit} disabled={saving || loading || !canSubmit(form, mode)} className="btn btn-primary flex items-center gap-1 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {mode === 'edit' ? 'Save changes' : 'Create employee'}
            </button>
          ) : (
            <button onClick={next} className="btn btn-primary flex items-center gap-1 disabled:opacity-50">
              Next <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
